import { Typography } from '@mui/material';

import type {
  ArtifactSummary,
  ShownFiles,
} from '@modules/_shared/components/chat/artifactFiles';
import { ArtifactThumbnail } from '@modules/_shared/components/chat/ArtifactThumbnail';
import {
  ChatFileRow,
  useChatFile,
  useFileStateText,
} from '@modules/_shared/components/chat/SavedFileCard';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

const GRID_THUMBNAIL_HEIGHT = 120;

const ImageTile = ({
  artifact,
  height,
}: {
  artifact: ArtifactSummary;
  height?: number;
}) => {
  const { colors } = useTheme();
  const file = useChatFile(artifact);
  const stateText = useFileStateText(file.state);
  return (
    <div css={{ minWidth: 0 }}>
      {file.state !== 'gone' && (
        <ArtifactThumbnail
          artifact={artifact}
          height={height}
          onOpen={() => void file.preview()}
        />
      )}
      {stateText != null && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: file.state === 'gone' ? colors.grey600 : colors.errorText,
          }}
        >
          {stateText}
        </Typography>
      )}
      {file.dialogs}
    </div>
  );
};

// Card under a finished show_files call: images as a grid of thumbnails (each
// opens the lightbox), other files as name / size / Preview / Download rows.
// The model only named the files; each one is fetched here with the user's
// own session.
export const ShowFilesCard = ({ shown }: { shown: ShownFiles }) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const single = shown.images.length === 1;
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {shown.title != null && (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {shown.title}
        </Typography>
      )}
      {shown.images.length > 0 && (
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: single
              ? '1fr'
              : 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 6,
          }}
        >
          {shown.images.map(artifact => (
            <ImageTile
              key={artifact.id}
              artifact={artifact}
              height={single ? undefined : GRID_THUMBNAIL_HEIGHT}
            />
          ))}
        </div>
      )}
      {shown.others.map(artifact => (
        <ChatFileRow key={artifact.id} artifact={artifact} />
      ))}
      {shown.missing.length > 0 && (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {shown.missing.length === 1 &&
          shown.images.length + shown.others.length === 0
            ? t('This file was deleted, or it was saved in another agency.')
            : t('Some of these files were deleted.')}
        </Typography>
      )}
    </div>
  );
};
